// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Severity design model (P1–P4), ported from the Operator Console mock's
// `sev()` / `sevIcon()`. Drives the card's left accent bar and the chip's
// glyph; the chip's own background/text color is a class from its sibling
// CSS module (SeverityChip.module.css), not computed here, since that value
// is local to the chip and nowhere else needs it. `accent` stays a raw
// var() value because it's consumed by two other things: AlarmCard's border
// and the chip's own SVG glyph fill/stroke.

import type { Severity } from '../types/alarm';

/** Glyph rendered inside the severity chip: filled disc, hollow ring, or dot. */
export type SevIconKind = 'filled' | 'ring' | 'dot';

export interface SeverityStyle {
  /** Long label (e.g. "Critical"). */
  label: string;
  /** Short tag shown in the chip (e.g. "P1"). */
  tag: Severity;
  /** Left accent bar + glyph color. */
  accent: string;
  /** Sort weight (1 = most severe). */
  order: number;
  /** SLA guidance text. */
  sla: string;
  /** SLA budget in seconds (0 = none). */
  slaSec: number;
  /** Which glyph the chip draws. */
  icon: SevIconKind;
}

/** P1–P4 severity map (verbatim from `sev()`). */
export const SEVERITY: Record<Severity, SeverityStyle> = {
  P1: {
    label: 'Critical',
    tag: 'P1',
    accent: 'var(--sev1)',
    order: 1,
    sla: 'Ack ≤ 30s',
    slaSec: 30,
    icon: 'filled',
  },
  P2: {
    label: 'High',
    tag: 'P2',
    accent: 'var(--sev2)',
    order: 2,
    sla: 'Ack 1–5m',
    slaSec: 300,
    icon: 'filled',
  },
  P3: {
    label: 'Medium',
    tag: 'P3',
    accent: 'var(--sev3)',
    order: 3,
    sla: 'Respond 5–15m',
    slaSec: 900,
    icon: 'ring',
  },
  P4: {
    label: 'Low',
    tag: 'P4',
    accent: 'var(--st-idle)',
    order: 4,
    sla: 'Routine review',
    slaSec: 0,
    icon: 'dot',
  },
  Unknown: {
    label: 'Unknown',
    tag: 'Unknown',
    accent: 'var(--text-helper)',
    order: 5,
    sla: 'Unknown',
    slaSec: 0,
    icon: 'dot',
  },
};

/** SVG circle params for a severity glyph, drawn as `<circle cx=8 cy=8>` in 16×16. */
export interface SevIconGeom {
  fill: string;
  stroke: string;
  strokeWidth: number;
  r: number;
}

/** Glyph geometry for a severity icon kind, colored by `accent`. */
export function sevIconGeom(kind: SevIconKind, accent: string): SevIconGeom {
  if (kind === 'ring') return { fill: 'transparent', stroke: accent, strokeWidth: 2, r: 4.4 };
  if (kind === 'dot') return { fill: accent, stroke: 'none', strokeWidth: 0, r: 3.2 };
  return { fill: accent, stroke: 'none', strokeWidth: 0, r: 5.6 };
}

/** Style descriptor for a severity (falls back to the neutral Unknown style). */
export function severityStyle(sev: Severity | string | null | undefined): SeverityStyle {
  return SEVERITY[sev as Severity] ?? SEVERITY.Unknown;
}

/** Left accent bar color for a severity. */
export function severityAccent(sev: Severity | string | null | undefined): string {
  return severityStyle(sev).accent;
}
